import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { ConfigProvider, Segmented } from 'antd';
import ViewHeader from 'components/common/ViewHeader/ViewHeader';
import { PADDOCK_KEY_PREFIX } from 'constants/routeConstants';
import { FARMS_URL_PARAM_LENS } from 'constants/routeConstants';
import {
  FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY,
  GROSS_MARGIN_INVALID_SCOPE_MESSAGE,
  GROSS_MARGIN_NO_DATA_MESSAGE,
  INPUT_DOC_LENS_ANALYSIS,
  INPUT_DOC_LENS_CASHFLOW,
  INPUT_DOC_LENS_LABELS,
  INPUT_DOC_LENS_MARGIN_BREAK_EVEN,
  INPUT_DOC_LENS_SUMMARY_2,
  INPUT_DOC_TAB_DIAGNOSTICS,
  VIEW_PURPOSE_INSIGHTS_ANALYSIS,
  VIEW_PURPOSE_INSIGHTS_CASHFLOW,
  VIEW_PURPOSE_INSIGHTS_MARGIN_BREAK_EVEN,
  VIEW_PURPOSE_INSIGHTS_SUMMARY,
} from 'constants/uiConstants';
import type { IFarm } from 'interfaces/IFarm';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInput } from 'interfaces/IInput';
import { RootState } from 'redux/Store';

import {
  InsightsLens,
  setGrossMarginCategory,
} from '../../../../redux/slices/InsightsSlice';
import useFarmsLinkNavigate from 'pages/FarmsPage/hooks/useFarmsLinkNavigate';
import WorkspaceEmptyState from '../WorkspaceEmptyState';

import AnalysisView from './AnalysisView';
import CashflowView from './cashflow/CashflowView';
import type { InsightsScopeLevel } from './insightUtils';
import CropAverageGmTile from './CropAverageGmTile';
import MarginBreakEvenView from './MarginBreakEvenView';
import SummaryReportView from './summary2/SummaryReportView';

// Passed straight through as CSS var() references rather than pre-resolved
// via getComputedStyle: this module can evaluate before tokens.css has been
// applied to the document (timing depends on chunk load order), in which
// case a getComputedStyle read at module scope permanently caches an empty
// string. AntD's CSS-in-JS just embeds whatever string it's given as the
// token's value, so a live var() reference is resolved correctly by the
// browser at paint time regardless of when this module ran.
const colorDarkGray = 'var(--color-dark-gray)';
const colorWhite = 'var(--color-white)';

// The old "Summary" lens is hidden (OP-2364) — "Summary 2" is relabelled
// below to "Summary" and takes its place as the default, but its underlying
// value/URL param is left unchanged so existing shared/bookmarked URLs keep
// working (see FARMS_URL_PARAM_LENS handling in useFarmsNavSync).
const LENS_PURPOSE: Record<InsightsLens, string> = {
  [INPUT_DOC_LENS_SUMMARY_2]: VIEW_PURPOSE_INSIGHTS_SUMMARY,
  [INPUT_DOC_LENS_ANALYSIS]: VIEW_PURPOSE_INSIGHTS_ANALYSIS,
  [INPUT_DOC_LENS_MARGIN_BREAK_EVEN]: VIEW_PURPOSE_INSIGHTS_MARGIN_BREAK_EVEN,
  [INPUT_DOC_LENS_CASHFLOW]: VIEW_PURPOSE_INSIGHTS_CASHFLOW,
} as Record<InsightsLens, string>;

const LENS_OPTIONS: { label: string; value: InsightsLens }[] = [
  {
    label: INPUT_DOC_LENS_LABELS[INPUT_DOC_LENS_SUMMARY_2],
    value: INPUT_DOC_LENS_SUMMARY_2,
  },
  {
    label: INPUT_DOC_LENS_LABELS[INPUT_DOC_LENS_ANALYSIS],
    value: INPUT_DOC_LENS_ANALYSIS,
  },
  {
    label: INPUT_DOC_LENS_LABELS[INPUT_DOC_LENS_MARGIN_BREAK_EVEN],
    value: INPUT_DOC_LENS_MARGIN_BREAK_EVEN,
  },
  {
    label: INPUT_DOC_LENS_LABELS[INPUT_DOC_LENS_CASHFLOW],
    value: INPUT_DOC_LENS_CASHFLOW,
  },
];

interface InputInsightsProps {
  inputs: IInput[];
  /** Tree-node-scoped but NOT tree-year-filtered — Cashflow does its own year-window matching (including cross-calendar-year financial years), so it can't rely on the tree's calendar-year-only filter. */
  cashflowInputs: IInput[];
  farms: IFarm[];
  gUnits: GUnit[];
  /** Name of whichever tree level is in scope — paddock, farm, or org. */
  scopeName?: string;
  /** Which tree level scopeName/inputs resolve to — lets a lens gate features that only make sense at a narrow-enough scope. */
  scopeLevel?: InsightsScopeLevel;
}

const InputInsights: React.FC<InputInsightsProps> = ({
  inputs,
  cashflowInputs,
  farms,
  gUnits,
  scopeName,
  scopeLevel,
}) => {
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const { activeLens, grossMarginCategory } = useSelector(
    (state: RootState) => state.insightsReducer
  );
  const navigateToFarmsView = useFarmsLinkNavigate();
  const selectedPaddockId = useSelector(
    (state: RootState) => state.farmReducer.selectedPaddockId
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
      <div className="mb-4">
        {/* Local ConfigProvider scoped to this Segmented only.
            AntD derives colours from tokens (not CSS), so overrides must go here.
            Keep in sync with --color-dark-gray and --color-white in tokens.css */}
        <ConfigProvider
          theme={{
            components: {
              Segmented: {
                trackBg: 'transparent',
                trackPadding: 2,
                itemSelectedBg: colorDarkGray,
                itemSelectedColor: colorWhite,
              },
            },
          }}
        >
          <Segmented
            className="insights-lens-segmented"
            options={LENS_OPTIONS.map((opt) => ({
              value: opt.value,
              label: (
                // Selected color/weight are set inline (rather than via the
                // ConfigProvider token + CSS class approach) since AntD's own
                // Segmented styles didn't reliably yield to either.
                <span
                  style={
                    activeLens === opt.value
                      ? { color: colorWhite, fontWeight: 600 }
                      : undefined
                  }
                >
                  {opt.label}
                </span>
              ),
            }))}
            value={activeLens}
            onChange={(value) =>
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (value === INPUT_DOC_LENS_SUMMARY_2) {
                  next.delete(FARMS_URL_PARAM_LENS);
                } else {
                  next.set(FARMS_URL_PARAM_LENS, value as string);
                }
                return next;
              })
            }
          />
        </ConfigProvider>
      </div>

      <div className="mb-4">
        <ViewHeader
          purpose={LENS_PURPOSE[activeLens]}
        />
      </div>

      {activeLens === INPUT_DOC_LENS_SUMMARY_2 && (
        <SummaryReportView
          inputs={inputs}
          gUnits={gUnits}
          scopeLevel={scopeLevel}
        />
      )}

      {activeLens === INPUT_DOC_LENS_ANALYSIS && (
        <AnalysisView
          inputs={inputs}
          farms={farms}
          gUnits={gUnits}
          scopeName={scopeName}
        />
      )}

      {activeLens === INPUT_DOC_LENS_CASHFLOW && (
        <CashflowView
          inputs={cashflowInputs}
          gUnits={gUnits}
          scopeName={scopeName}
        />
      )}

      {activeLens === INPUT_DOC_LENS_MARGIN_BREAK_EVEN &&
        (() => {
          // A crop is valid too (one crop, so yields and prices average
          // meaningfully); a farm or org mixes crops, so it isn't.
          const isScopeValid =
            scopeLevel === 'input' ||
            scopeLevel === 'paddock' ||
            scopeLevel === 'crop';

          if (isScopeValid && inputs.length > 0) {
            return (
              <div className="flex flex-col items-start gap-6">
                <CropAverageGmTile
                  inputs={inputs}
                  scopeLevel={scopeLevel}
                  activeCategory={grossMarginCategory}
                  gUnits={gUnits}
                />
                <MarginBreakEvenView
                  inputs={inputs}
                  activeCategory={grossMarginCategory}
                  onCategoryChange={(cat) =>
                    dispatch(setGrossMarginCategory(cat))
                  }
                  gUnits={gUnits}
                  areaBasis={scopeLevel === 'crop' ? 'total' : 'average'}
                  onOpenMultiYearTrend={
                    scopeLevel !== 'crop' && selectedPaddockId
                      ? () =>
                          navigateToFarmsView({
                            nodeKey: `${PADDOCK_KEY_PREFIX}${selectedPaddockId}`,
                            tab: INPUT_DOC_TAB_DIAGNOSTICS,
                            diagnosticsLens:
                              FARM_DIAGNOSTICS_LENS_FINANCIAL_EFFICIENCY,
                          })
                      : undefined
                  }
                />
              </div>
            );
          }

          // Two distinct reasons the lens isn't shown: the tree selection is
          // too broad (org/farm — margin and break-even only make sense for one
          // crop, paddock or plan, so the message points to the tree),
          // or the scope is valid but that paddock/plan simply has no data to
          // compute a margin from.
          return (
            <WorkspaceEmptyState
              message={
                isScopeValid
                  ? GROSS_MARGIN_NO_DATA_MESSAGE
                  : GROSS_MARGIN_INVALID_SCOPE_MESSAGE
              }
            />
          );
        })()}
    </div>
  );
};

export default InputInsights;
